import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { TourStep } from "./tourSteps";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft, Sparkles, Rocket } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface OnboardingTourProps {
  steps: TourStep[];
  currentStep: number;
  isActive: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const OnboardingTour = ({
  steps,
  currentStep,
  isActive,
  onNext,
  onPrev,
  onSkip,
  onFinish,
}: OnboardingTourProps) => {
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const step = steps[currentStep];

  const measureTarget = useCallback(() => {
    if (!step || step.type !== "spotlight" || !step.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      });
      // Scroll into view if needed
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!isActive) return;
    measureTarget();
    const handleResize = () => measureTarget();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [isActive, currentStep, measureTarget]);

  if (!isActive || !step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const isModal = step.type === "modal";

  const handleNext = () => {
    if (isLast) {
      onFinish();
    } else {
      onNext();
    }
  };

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect || isModal) return {};
    const padding = 16;
    const tooltipWidth = 360;

    // Position below by default, above if too close to bottom
    const spaceBelow = window.innerHeight - (targetRect.top + targetRect.height);
    const positionAbove = spaceBelow < 220;

    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    // Clamp to viewport
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    if (positionAbove) {
      return {
        position: "fixed",
        top: targetRect.top - padding - 12,
        left,
        width: tooltipWidth,
        transform: "translateY(-100%)",
      };
    }
    return {
      position: "fixed",
      top: targetRect.top + targetRect.height + 12,
      left,
      width: tooltipWidth,
    };
  };

  const overlay = (
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9998]"
        style={{ pointerEvents: "auto" }}
      >
        {/* Dimmed overlay with spotlight hole */}
        {isModal ? (
          <div className="absolute inset-0 bg-black/60" />
        ) : targetRect ? (
          <div
            className="absolute inset-0"
            style={{
              boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.6)`,
              position: "fixed",
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
              borderRadius: 16,
              pointerEvents: "none",
              zIndex: 9998,
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-black/60" />
        )}

        {/* Click blocker behind the tooltip but not on the target */}
        <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />

        {/* Modal or Tooltip card */}
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className="z-[9999]"
          style={
            isModal
              ? {
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: Math.min(440, window.innerWidth - 32),
                }
              : getTooltipStyle()
          }
        >
          <div className="bg-card border-2 border-primary/30 rounded-2xl shadow-2xl shadow-primary/10 p-6 relative">
            {/* Skip button */}
            <button
              onClick={onSkip}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Skip tour"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Icon for modals */}
            {isModal && (
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  {isFirst ? (
                    <Rocket className="w-8 h-8 text-primary" />
                  ) : (
                    <Sparkles className="w-8 h-8 text-primary" />
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            <h3 className="font-heading font-bold text-lg text-foreground mb-2 pr-6 text-center">
              {step.title}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed text-center mb-1">
              {step.description}
            </p>
            {step.tagline && (
              <p className="text-sm font-semibold text-primary text-center mt-2">
                {step.tagline}
              </p>
            )}

            {/* Step counter */}
            <div className="flex items-center justify-between mt-5">
              <span className="text-xs text-muted-foreground">
                {currentStep + 1} of {steps.length}
              </span>

              <div className="flex items-center gap-2">
                {!isFirst && (
                  <Button variant="ghost" size="sm" onClick={onPrev} className="gap-1">
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                  </Button>
                )}
                <Button size="sm" onClick={handleNext} className="gap-1">
                  {isLast ? (
                    "Let's Go!"
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mt-3">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? "w-6 bg-primary"
                      : i < currentStep
                      ? "w-1.5 bg-primary/40"
                      : "w-1.5 bg-muted-foreground/20"
                  }`}
                />
              ))}
            </div>

            {/* Skip all link */}
            {!isLast && (
              <button
                onClick={onSkip}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors"
              >
                Skip tour
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(overlay, document.body);
};
